import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import queryString from 'query-string';

import AuthButton from 'components/base/AuthButton';
import { OnlyBackButton } from 'components/base/BottomStepComponent';
import ContentSections from 'components/base/ContentSections';
import CustomBreadcrumb from 'components/base/CustomBreadcrumb';
import CustomListComponent from 'components/base/CustomListComponent';
import Loading from 'components/base/Loading';
import MembershipInfoCard from 'components/customer/MembershipInfoCard';
import MessageOverviewTableSection from 'components/message/MessageOverviewTableSection';
import { APIStatus, DISPLAY_MESSAGE_TARGET_TYPE, MessageChannel, MessageTag } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { ImportResource } from 'models/DownloadImportModel';

import { TimeFormater, formatDate } from 'utils/TimeFormatUtil';
import { getDisplayPerformanceFields } from 'containers/engagement/message/MessagePerformance';

const mapPropsToState = (state) => ({
  selectedMessage: state.messageList.message,
  status: state.loading.status,
  languages: state.language.allList,
});

function MessageOverview({ selectedMessage, status, languages }) {
  const params = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const message = {
    ...selectedMessage,
    displayChannels: selectedMessage.channels?.toString(),
    relatedCampaignName: selectedMessage.relatedCampaign?.pk
      ? `[ID: ${selectedMessage.relatedCampaign?.pk}] ${selectedMessage.relatedCampaign?.name}`
      : '-',
    displayTargetType: DISPLAY_MESSAGE_TARGET_TYPE?.[selectedMessage?.targetType] || selectedMessage?.targetType,
    displayTargetLevels: (selectedMessage.targetedLevels || [])
      .map((item) => item.levelName)
      .toString(),
    displayTargetedStaticSegments: (selectedMessage.targetedStaticSegments || [])
      .map((item) => item.name)
      .toString(),
    displayTargetedDynamicSegments: (selectedMessage.targetedDynamicSegments || [])
      .map((item) => item.name)
      .toString(),
    deliveryDate: formatDate(
      selectedMessage.scheduledDate,
      TimeFormater.dayMonthYearWeekTimeA,
    ),
    displaySendToAll: selectedMessage?.sendToAll ? 'Yes' : 'No',
    displayRecipientsFollowCampaign: selectedMessage.recipientsFollowCampaign ? 'Yes' : 'No',
  };

  useEffect(() => {
    dispatch({
      type: 'messageList/getMessage',
      payload: {
        messagePK: params.id,
      },
    });
    dispatch({
      type: 'language/getList',
      payload: {
        isSelectorLoad: true,
      },
    });
  }, [dispatch, params.id]);

  useEffect(() => {
    return () => {
      dispatch({
        type: 'createMessage/clearData',
        payload: {},
      });
      dispatch({
        type: 'messageList/clearData',
        payload: {},
      });
    };
  }, [dispatch]);

  const renderTabForLanguage = (language) => {
    return [
      <MessageOverviewTableSection message={message} language={language} />,
      <MembershipInfoCard
        title={'PROPERTIES'}
        data={message}
        fields={[
          [
            {
              title: 'Start with a template',
              field: 'displayIsStartWithTemplate',
            },
          ],
          [
            {
              title: 'Template',
              field: 'displayTemplateName',
            },
          ],
          [
            {
              title: 'Name',
              field: 'name',
            },
          ],
          [
            {
              title: 'Message channel',
              field: 'displayChannels',
              isRichText: true,
            },
          ],
          [
            {
              title: 'Related campaign',
              field: 'relatedCampaignName',
            },
          ],
          [
            {
              title: 'Is this a direct marketing message?',
              field: 'isDirectMarketing',
              isBoolean: true,
            },
          ],
          [
            {
              title: 'Send to every customer (including non-member)',
              field: 'displaySendToAll',
            },
          ],
          [
            {
              title: 'Message recipients follow campaign',
              field: 'displayRecipientsFollowCampaign',
            },
          ],
          [{ title: 'Target customer', field: 'displayTargetType' }],
          [{ title: 'Level', field: 'displayTargetLevels' }],
          [{ title: 'Static segment', field: 'displayTargetedStaticSegments' }],
          [{ title: 'Dynamic segment', field: 'displayTargetedDynamicSegments' }],
          [
            {
              title: 'Target customers',
              field: 'numberOfTargetCustomers',
            },
          ],
          [{ title: 'Delivery Time', field: 'deliveryDate' }],
        ]}
      />,
      message?.channels?.length > 0 ? (
        <MembershipInfoCard
          title={'PERFORMANCE'}
          data={message}
          fields={getDisplayPerformanceFields(message)}
        />
      ) : null,
      <MembershipInfoCard
        title="GENERAL"
        data={message}
        fields={[
          [{ title: 'Status', field: 'status' }],
          [{ title: 'Creation time', field: 'displayCreationDate' }],
          [{ title: 'Last modified time', field: 'displayLastModifiedDate' }],
          [{ title: 'Last modified by', field: 'displayLastModifiedBy' }],
        ]}
      />
    ];
  };

  const tabs = [
    {
      name: '',
      content:
        status === APIStatus.calling ? (
          <Loading />
        ) : (
          <div className="scroll-container-common">
            <ContentSections
              languageTabContent={{
                containers: languages?.map((item) => ({
                  container: renderTabForLanguage(item.code),
                  key: item.code,
                  title: item.sourceName,
                })),
              }}
              hidePreview={true}
            />
          </div>
        ),
    },
  ];

  const exportCSVAction = () => {
    dispatch({
      type: 'downloadAndImport/createDownloadTask',
      payload: {
        from: ImportResource.sentMessageRecord,
        related: {
          message: params.id,
        },
      },
    });
  };

  const buttons = [
    [MessageTag.sent, MessageTag.error].includes(selectedMessage.status) ? (
      <AuthButton
        title="Export .csv"
        action={exportCSVAction}
        requires={PermissionCodes.addExportjob}
      />
    ) : null,
    [
      MessageTag.draft,
      MessageTag.pendingForApproval,
      MessageTag.scheduled,
    ].includes(selectedMessage.status) ? (
      <AuthButton
        title="Edit"
        action={() => history.push('edit/')}
        requires={PermissionCodes.changeMessage}
      />
    ) : null,
  ];

  return (
    <>
      <CustomListComponent
        caution={{
          detail: '', //view message
          title: 'View Message',
        }}
        breadcrumb={<CustomBreadcrumb />}
        hideTab={true}
        tabs={tabs}
        buttons={buttons}
      />
      <OnlyBackButton
        backAction={() => {
          history.push({
            pathname: '/messages',
            search: queryString.stringify({
              page: 1,
              rank: true,
              search: '',
            }),
          });
        }}
      />
    </>
  );
}

export default connect(mapPropsToState)(MessageOverview);
